import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Text,
  useToast,
  Link,
  Stack,
} from '@chakra-ui/react';

import { nextBillingDate } from '../api';
import { useQueryClient } from 'react-query';
import { useModalManagerContext } from '../../components/ModalManager/ModalManagerContext';
import { useGlobalContext } from '../state/GlobalContext';
import dayjs from 'dayjs';

const RescheduleModal: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { loadPreviousFlowStep, loadNextFlowStep, onClose } =
    useModalManagerContext();
  const { customerId, subscriptionId, authToken } = useGlobalContext();

  const toast = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [nextDate, setNextDate] = useState(
    dayjs().add(1, 'day').format('YYYY-MM-DD'),
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    toast.closeAll();
    setIsLoading(true);
    const subscription = queryClient.getQueryData<Subscription>([
      'subscription',
      customerId,
      subscriptionId,
    ]);

    try {
      await nextBillingDate(
        dayjs(nextDate, 'YYYY-MM-DD'),
        customerId,
        subscriptionId,
        authToken,
        'UPDATED_ORDER_DATE',
      );
      setIsLoading(false);
      const newDate = dayjs(nextDate, 'YYYY-MM-DD')
        .toDate()
        .toLocaleDateString();
      toast({
        isClosable: true,
        position: 'top',
        status: 'success',
        title: t('cancelModal.skip.success', {
          date: newDate,
        }),
        variant: 'success',
      });
      queryClient.setQueryData(['subscription', customerId, subscriptionId], {
        ...subscription,
        NextBillingDate: newDate,
      });
      onClose();
    } catch (err) {
      setIsLoading(false);
      toast({
        isClosable: true,
        position: 'top',
        status: 'error',
        title: (
          <Text color="white">
            {t('cancelModal.error')}{' '}
            <Link onClick={onSubmit}>{t('cancelModal.tryAgain')}</Link>
          </Text>
        ),
        variant: 'error',
      });
      console.error(err);
    }
  };

  return (
    <>
      <ModalHeader>{t('cancelModal.reschedule.header')}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Text>{t('cancelModal.reschedule.description')}</Text>
        <form id="next-date-form" onSubmit={onSubmit}>
          <Stack spacing={10} direction="row" align="center" justify="start">
            <input
              type="date"
              id="start"
              name="next-date"
              value={nextDate}
              required
              min={dayjs().add(1, 'day').format('YYYY-MM-DD')}
              onChange={(e) => setNextDate(e.target.value)}
            />
            <Button
              type="submit"
              form="next-date-form"
              isLoading={isLoading}
              disabled={!nextDate || isLoading}
              variant="outlinedBig"
            >
              {t('cancelModal.reschedule.confirmReschedule')}
            </Button>
          </Stack>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          mr="4"
          onClick={loadPreviousFlowStep}
          disabled={isLoading}
          variant="outlinedBig"
        >
          {t('cancelModal.back')}
        </Button>
        <Button
          onClick={loadNextFlowStep}
          disabled={isLoading}
          variant="blackBig"
        >
          {t('cancelModal.continueWithCancellation')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default RescheduleModal;
